import { createPopper } from '@popperjs/core';
import type { Instance as PopperInstance, Placement } from '@popperjs/core';
import { delegate } from 'common/delegate';

const TRIGGER_SELECTOR = '[data-tooltip]';
const HOVER_DELAY_MS = 200;

/**
 * How long after a tap the compatibility mouse events the browser emits for
 * it are ignored. Long enough to cover the ~300ms those can lag the tap by.
 */
const TOUCH_SUPPRESS_MS = 500;

/**
 * Matches the CSS transition duration on `.tooltip` in
 * `js/styles/rails-styles/modules/_tooltip.scss`. Keep both in sync.
 */
const FADE_MS = 150;

/**
 * Where Popper may move a side-placed tip that does not fit. The opposite side
 * alone is not enough: beside a trigger as wide as a phone's viewport neither
 * side has room, and the tip would be laid out past the edge of the page.
 */
const SIDE_FALLBACK_PLACEMENTS: Partial<Record<Placement, Array<Placement>>> = {
  left: ['right', 'bottom', 'top'],
  right: ['left', 'bottom', 'top']
};

type OpenTooltip = {
  trigger: HTMLElement;
  tip: HTMLElement;
  popper: PopperInstance;
  titleText: string;
  removalObserver: MutationObserver;
};

let current: OpenTooltip | null = null;
let hoverTimer: ReturnType<typeof setTimeout> | null = null;
let lastTouchAt = 0;
let nextTipId = 0;
let initialized = false;

export default function initializeTooltips(): void {
  if (initialized) return;
  initialized = true;

  // Capture phase, because `mouseenter`/`mouseleave` do not bubble — a
  // delegated listener on `document` only sees them on the way down.
  delegate(document, 'mouseenter', TRIGGER_SELECTOR, handleMouseEnter, true);
  delegate(document, 'mouseleave', TRIGGER_SELECTOR, handleMouseLeave, true);
  // Passive: nothing listening to a touch may call `preventDefault()`.
  // Cancelling a touchstart on a trigger also cancels the tap that focuses an
  // `<input data-tooltip>` and the scroll gesture that begins on one.
  const passiveCapture = { capture: true, passive: true };
  document.addEventListener('touchstart', recordTouch, passiveCapture);
  document.addEventListener('touchend', recordTouch, passiveCapture);
  delegate(
    document,
    'touchstart',
    TRIGGER_SELECTOR,
    handleTriggerTouch,
    passiveCapture
  );
  document.addEventListener('click', handleClick, true);
  // `close` does not bubble, so it is caught on the way down.
  document.addEventListener('close', handleDialogClose, true);
}

function handleMouseEnter(event: MouseEvent, trigger: Element): void {
  if (!(trigger instanceof HTMLElement)) return;
  if (current?.trigger === trigger) return;

  // A tap makes the browser emit compatibility mouse events on the same
  // element shortly afterwards. Without this window a tap that closes a
  // tooltip is immediately followed by a synthetic `mouseenter` that reopens
  // it as a hover tooltip — which then never closes, because a touch device
  // sends no `mouseleave`.
  if (Date.now() - lastTouchAt < TOUCH_SUPPRESS_MS) return;

  // Entering the trigger's own children is not a new hover. Native
  // `mouseenter` fires on every element boundary the pointer crosses, so a
  // pointer travelling across a trigger's inner icon would otherwise restart
  // the delay on each boundary and the tooltip would never appear.
  const related = event.relatedTarget;
  if (related instanceof Node && trigger.contains(related)) return;

  if (hoverTimer) clearTimeout(hoverTimer);
  hoverTimer = setTimeout(() => {
    hoverTimer = null;
    if (!trigger.isConnected) return;
    openTooltip(trigger, { touch: false });
  }, HOVER_DELAY_MS);
}

function handleMouseLeave(event: MouseEvent, trigger: Element): void {
  if (!(trigger instanceof HTMLElement)) return;

  // A tap away from the trigger makes the browser report the pointer leaving
  // it. Closing on that would take the tip out from under the tap before its
  // click arrives; `handleClick` closes it once the click has landed.
  if (Date.now() - lastTouchAt < TOUCH_SUPPRESS_MS) return;

  // Native mouseleave/mouseenter fire on every element boundary the pointer
  // crosses, including a trigger's own element children — so moving within
  // the trigger's bounds (e.g. onto a child icon) fires this handler too.
  // Only treat it as a real exit if the pointer actually left the trigger.
  const related = event.relatedTarget;
  if (related instanceof Node && trigger.contains(related)) return;

  if (hoverTimer) {
    clearTimeout(hoverTimer);
    hoverTimer = null;
  }
  if (current?.trigger === trigger) closeTooltip();
}

/**
 * Opens and closes on tap without cancelling the tap itself — the browser
 * still delivers focus, clicks and scrolling. The compatibility mouse events
 * a tap also produces are gated by `lastTouchAt` in `handleMouseEnter`.
 */
function handleTriggerTouch(_event: TouchEvent, trigger: Element): void {
  if (!(trigger instanceof HTMLElement)) return;
  // Anchors carry a real action of their own (navigation, Rails UJS), and a
  // tooltip opening under the finger competes with the tap that fires it.
  if (trigger.closest('a[href]')) return;

  if (hoverTimer) {
    clearTimeout(hoverTimer);
    hoverTimer = null;
  }
  if (current?.trigger === trigger) {
    closeTooltip();
  } else {
    openTooltip(trigger, { touch: true });
  }
}

/**
 * Any touch, on a trigger or not, opens the window in which `handleMouseEnter`
 * ignores the compatibility mouse events a tap produces. Those follow the end
 * of a touch, not its start, so the window is renewed on both: a press held
 * for most of `TOUCH_SUPPRESS_MS` would otherwise have used it up before they
 * arrive.
 */
function recordTouch(): void {
  lastTouchAt = Date.now();
}

/**
 * Closes the open tooltip on a click anywhere but its own trigger, the tooltip
 * itself included. It waits for the click rather than acting on the touch that
 * starts the tap: a closed tip stops being hit-testable at once, and a tap on
 * it would land on whatever sits beneath.
 */
function handleClick(event: MouseEvent): void {
  if (!current || !(event.target instanceof Node)) return;
  // The trigger's own click belongs to the tap or hover that opened the tip.
  if (current.trigger.contains(event.target)) return;
  closeTooltip();
}

/**
 * A tip inside a dialog is hidden along with it, and would come back, still
 * open, the next time the dialog opens.
 */
function handleDialogClose(event: Event): void {
  if (!current || !(event.target instanceof Node)) return;
  if (event.target.contains(current.trigger)) closeTooltip();
}

function placementFor(trigger: Element): Placement {
  if (trigger.classList.contains('tip-top')) return 'top';
  if (trigger.classList.contains('tip-left')) return 'left';
  if (trigger.classList.contains('tip-right')) return 'right';
  return 'bottom';
}

function openTooltip(
  trigger: HTMLElement,
  { touch }: { touch: boolean }
): void {
  if (current) closeTooltip();

  const id = `tooltip-${nextTipId}`;
  nextTipId += 1;

  // Blanking `title` here (restored in closeTooltip) suppresses the native
  // browser title-tooltip so it can't render alongside the custom `.tooltip`
  // span on a long hover.
  const titleText = trigger.getAttribute('title') ?? '';
  trigger.setAttribute('title', '');

  const nub = document.createElement('span');
  nub.className = 'nub';

  const tip = document.createElement('span');
  tip.id = id;
  tip.setAttribute('role', 'tooltip');
  tip.className = 'tooltip';
  tip.appendChild(document.createTextNode(titleText));
  tip.appendChild(nub);

  if (touch) {
    // Only a tip opened by touch takes pointer events: it has to receive the
    // tap that closes it. See `.is-tappable` in `modules/_tooltip.scss`.
    tip.classList.add('is-tappable');
    const tapToClose = document.createElement('span');
    tapToClose.className = 'tap-to-close';
    tapToClose.textContent = __('foundation.tooltip.touch_close_text');
    tip.appendChild(tapToClose);
  }

  // A modal `<dialog>` paints in the top layer, above everything in `body`
  // whatever its `z-index`, so a tip for a trigger inside one goes inside it.
  (trigger.closest('dialog') ?? document.body).appendChild(tip);
  trigger.setAttribute('aria-describedby', id);
  trigger.classList.add('open');

  const placement = placementFor(trigger);
  const popper = createPopper(trigger, tip, {
    placement,
    modifiers: [
      { name: 'offset', options: { offset: [0, 10] } },
      {
        name: 'flip' as const,
        options: { fallbackPlacements: SIDE_FALLBACK_PLACEMENTS[placement] }
      },
      { name: 'preventOverflow', options: { padding: 8 } },
      { name: 'arrow', options: { element: nub, padding: 5 } }
    ]
  });

  // A trigger leaves the DOM when any ancestor is removed, not only its
  // parent — `EditOpeningHours.tsx` collapsing its inputs takes one out from
  // several levels up — so the observer covers the whole document.
  const removalObserver = new MutationObserver(() => {
    if (!trigger.isConnected) closeTooltip();
  });
  removalObserver.observe(document.body, { childList: true, subtree: true });

  current = { trigger, tip, popper, titleText, removalObserver };

  // Commits the "closed" (opacity: 0) state to the DOM before adding
  // `is-open`, so the opacity transition actually plays instead of the
  // browser collapsing both states into one frame with no visible fade.
  // eslint-disable-next-line no-unused-expressions
  tip.offsetHeight;
  tip.classList.add('is-open');
}

function closeTooltip(): void {
  if (!current) return;
  const { trigger, tip, popper, titleText, removalObserver } = current;
  current = null;

  removalObserver.disconnect();
  trigger.classList.remove('open');
  trigger.removeAttribute('aria-describedby');
  if (titleText) {
    trigger.setAttribute('title', titleText);
  } else {
    trigger.removeAttribute('title');
  }
  tip.classList.remove('is-open');

  setTimeout(() => {
    popper.destroy();
    tip.remove();
  }, FADE_MS);
}
